import React, { useMemo, useCallback } from 'react';
import { View, Text, StyleSheet, ScrollView } from 'react-native';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { useAppSelector } from '@/store';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { REDESIGN_TYPOGRAPHY } from '@/constants/residents/homeConstants';
import { selectLeaseIds, isUserLeaseHolder } from '@/store/selectors/userSelectors';
import { linkedAcctsConnections, linkedAcctsConnectionsLoading, selectAllLeaseIds } from '@/store/selectors/linkedAccountsSelectors';
import { useGetAutoPayStatusQuery, useCancelAutoPayMutation, useGetLeaseQuery, useGetPaymentMethodsQuery } from '@/store/api';
import { useToast } from '@/contexts/admin/ToastContext';
import { getPaymentMethodShortLabel } from '@/utils/residents/paymentUtils';
import { getDisplayName, getInitials } from '@/utils/residents/utils';
import { AutopayCard } from '@/components/residents/redesign/payments';
import { ConfirmationModal } from '@/components/residents/marketplace/ConfirmationModal';
import { SectionHeader } from '@/components/residents/redesign/SectionHeader';
import { GlassCard } from '@/components/residents/redesign/GlassCard';
import { strings } from '@/constants/residents/strings';

/**
 * Desktop Autopay View (Account Settings)
 * Uses the unified redesign AutopayCard component.
 */
export const AutopayView: React.FC = () => {
  const { colors } = useThemeColors();
  const navigation = useNavigation<any>();
  const { showSuccess, showError } = useToast();
  const [refreshNonce, setRefreshNonce] = React.useState(0);
  const [removeConfirm, setRemoveConfirm] = React.useState<{ leaseId: number; autopayId: number } | null>(null);

  const ownLeaseIds = useAppSelector(selectLeaseIds);
  const connectionLeaseIds = useAppSelector(selectAllLeaseIds);
  const connections = useAppSelector(linkedAcctsConnections);
  const connectionsFetchLoading = useAppSelector(linkedAcctsConnectionsLoading);
  const userIsLeaseHolder = useAppSelector(isUserLeaseHolder);
  const authUser = useAppSelector((s) => (s as any).auth?.user);

  const allLeaseIds = useMemo(() => {
    const combined = [...ownLeaseIds];
    connectionLeaseIds.forEach((id) => {
      if (!combined.includes(id)) combined.push(id);
    });
    return combined;
  }, [ownLeaseIds, connectionLeaseIds]);

  const connectionUserByLeaseId = useMemo(() => {
    const map = new Map<number, any>();
    (connections || []).forEach((conn: any) => {
      (conn?.leaseIds || []).forEach((id: number) => map.set(id, conn.user));
    });
    return map;
  }, [connections]);

  const { refetch: autoPayStatusRefetch } = useGetAutoPayStatusQuery();
  const { data: methodsData } = useGetPaymentMethodsQuery();
  const paymentMethods: any[] = useMemo(() => methodsData?.response || [], [methodsData]);
  const [cancelAutoPay] = useCancelAutoPayMutation();

  const handleRefresh = useCallback(() => {
    autoPayStatusRefetch();
    setRefreshNonce((n) => n + 1);
  }, [autoPayStatusRefetch]);

  useFocusEffect(
    useCallback(() => {
      handleRefresh();
      return () => {};
    }, [handleRefresh])
  );

  const handleManage = useCallback((leaseId: number) => {
    navigation.navigate('AutoPayWizard', { leaseId });
  }, [navigation]);

  const handleRemove = useCallback((_leaseId: number, autopayId: number) => {
    setRemoveConfirm({ leaseId: _leaseId, autopayId });
  }, []);

  const handleRemoveConfirmed = useCallback(async () => {
    if (!removeConfirm?.autopayId) return;
    setRemoveConfirm(null);
    try {
      await cancelAutoPay(removeConfirm.autopayId).unwrap();
      handleRefresh();
      showSuccess('Autopay removed successfully');
    } catch (err: any) {
      showError(err?.data?.message || err?.data?.error?.message || 'Failed to remove autopay');
    }
  }, [removeConfirm, cancelAutoPay, handleRefresh, showSuccess, showError]);

  const hasNoLeases = !userIsLeaseHolder && allLeaseIds.length === 0;

  if (hasNoLeases && !connectionsFetchLoading) {
    return (
      <View style={styles.root}>
        <View style={styles.content}>
          <SectionHeader title="Autopay" />
          <GlassCard style={styles.emptyCard}>
            <Text style={[styles.emptyTitle, { color: colors.text.primary }]}>{strings.account.autopayEmptyTitle}</Text>
            <Text style={[styles.emptyMessage, { color: colors.text.muted }]}>{strings.account.autopayEmptyMessage}</Text>
          </GlassCard>
        </View>
      </View>
    );
  }

  return (
    <>
      <View style={styles.root}>
        <ScrollView style={styles.body} contentContainerStyle={styles.bodyInner}>
          <SectionHeader title="Autopay" />
          <View style={styles.leasesList}>
            {allLeaseIds.map((leaseId) => (
              <DesktopLeaseAutopayCard
                key={leaseId}
                leaseId={leaseId}
                isOwnLease={ownLeaseIds.includes(leaseId)}
                connectionUser={connectionUserByLeaseId.get(leaseId)}
                authUser={authUser}
                paymentMethods={paymentMethods}
                refreshNonce={refreshNonce}
                onManage={() => handleManage(leaseId)}
                onRemove={handleRemove}
              />
            ))}
          </View>
        </ScrollView>
      </View>

      <ConfirmationModal
        visible={removeConfirm !== null}
        message="Are you sure you want to remove autopay? You will need to set it up again if you change your mind."
        onCancel={() => setRemoveConfirm(null)}
        onProceed={handleRemoveConfirmed}
        cancelText="No, cancel"
        proceedText="Yes, remove"
      />
    </>
  );
};

interface DesktopLeaseAutopayCardProps {
  leaseId: number;
  isOwnLease: boolean;
  connectionUser?: any;
  authUser?: any;
  paymentMethods: any[];
  refreshNonce: number;
  onManage: () => void;
  onRemove: (leaseId: number, autopayId: number) => void;
}

const DesktopLeaseAutopayCard: React.FC<DesktopLeaseAutopayCardProps> = React.memo(({
  leaseId,
  isOwnLease,
  connectionUser,
  authUser,
  paymentMethods,
  refreshNonce,
  onManage,
  onRemove,
}) => {
  const { data: leaseResp, refetch } = useGetLeaseQuery(
    { leaseId, includeTransactions: false, includePayments: true },
    { skip: !leaseId }
  );

  React.useEffect(() => {
    refetch();
  }, [refreshNonce, refetch]);

  const lease = (leaseResp as any)?.response?.lease;

  const propertyName = lease?.property?.marketingName || '—';
  const users = lease?.users || [];
  const primaryUser = users?.find((u: any) => u?.LeaseCustomer?.customerType === 'primary');
  const displayUser = isOwnLease
    ? (users?.find((u: any) => u?.id === authUser?.id) || primaryUser || users?.[0])
    : (connectionUser || primaryUser || users?.[0]);

  const displayName = getDisplayName(displayUser?.firstName, displayUser?.lastName) || 'Resident';
  const initials = getInitials(displayUser?.firstName, displayUser?.lastName) || 'R';

  const recurringPayments = lease?.recurringPayments || [];
  const autopay = recurringPayments[0];
  const hasAutopay = Boolean(autopay);

  const paymentMethodLabel = useMemo(() => {
    if (!autopay?.sourceId) return '—';
    const method = paymentMethods.find((m: any) => {
      return (Number(m.sourceId) || m.id) === Number(autopay.sourceId);
    });
    return method ? getPaymentMethodShortLabel(method) : '—';
  }, [autopay?.sourceId, paymentMethods]);

  const isSetupDisabled = useMemo(() => {
    const endDate = lease?.endDate ? new Date(lease.endDate) : null;
    if (!endDate || isNaN(endDate.getTime())) return false;
    const firstOfEndMonth = new Date(endDate);
    firstOfEndMonth.setDate(1);
    return firstOfEndMonth < new Date();
  }, [lease?.endDate]);

  if (!lease) return null;

  const formatDate = (iso?: string): string => {
    if (!iso) return 'End of lease';
    try {
      return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return '—';
    }
  };

  const amountText = autopay?.amount
    ? `$${Number(autopay.amount).toFixed(2)}`
    : 'Full balance';

  return (
    <AutopayCard
      displayName={displayName}
      initials={initials}
      photoUrl={displayUser?.photoUrl}
      propertyName={propertyName}
      autopayEnabled={hasAutopay}
      amount={hasAutopay ? amountText : undefined}
      paymentMethodLabel={hasAutopay ? paymentMethodLabel : undefined}
      startDate={hasAutopay ? formatDate(autopay?.startDate || autopay?.createdAt) : undefined}
      endDate={hasAutopay ? formatDate(autopay?.endDate) : undefined}
      onSetup={onManage}
      onEdit={onManage}
      onRemove={hasAutopay ? () => onRemove(leaseId, autopay.id) : undefined}
      isSetupDisabled={isSetupDisabled}
    />
  );
});

DesktopLeaseAutopayCard.displayName = 'DesktopLeaseAutopayCard';

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  content: {
    padding: 24,
  },
  body: {
    flex: 1,
  },
  bodyInner: {
    padding: 24,
    gap: 12,
  },
  leasesList: {
    gap: 12,
  },
  emptyCard: {
    alignItems: 'center',
    paddingVertical: 32,
  },
  emptyTitle: {
    ...REDESIGN_TYPOGRAPHY.cardTitleLarge,
    marginBottom: 6,
    textAlign: 'center',
  },
  emptyMessage: {
    ...REDESIGN_TYPOGRAPHY.body,
    textAlign: 'center',
    lineHeight: 20,
    maxWidth: 260,
  },
});

export default AutopayView;
